// Support Article Hub - Data Structure
// Categories and articles for the agent portal help center

export const supportCategories = [
    {
      slug: 'getting-started',
      title: 'Getting Started',
      icon: 'Compass',
      description: 'New to Lauft? Start here.',
      color: '#6366f1', // Indigo
      articles: [
        {
          slug: 'welcome-to-agent-portal',
          title: 'Welcome to the Agent Portal',
          summary: 'A quick overview of your new home base for managing buyers and growing your business.',
          content: `
  # Welcome to the Agent Portal
  
  Your Agent Portal is the command center for managing your Lauft business. Here's what you can do:
  
  ## Dashboard Overview
  
  When you log in, you'll land on your **Dashboard** — a bird's-eye view of your activity:
  
  - **Your Profile Card** — Your photo, contact info, and referral link
  - **Quick Stats** — Assigned buyers, available leads, matches sent, and invited users
  - **Available Users** — A preview of leads ready to claim
  - **Recent Properties** — Your latest listings at a glance
  
  ## Navigation
  
  Use the sidebar on the left to navigate between sections:
  
  - **Dashboard** — Your home base
  - **Additional Listings** — Manage properties beyond your MLS sync
  - **Add Property** — Manually add a new listing
  - **Scheduled Showings** — View and manage showing requests
  - **User Management** — Invite and manage your buyers
  - **Available Users** — Browse and claim new leads
  - **Conversations** — Message your buyers directly
  
  ## Getting Help
  
  Stuck? You're in the right place! Browse our help articles by category, or use the search bar to find specific topics.
  
  If you can't find what you need, reach out to our support team through the chat widget on your dashboard.
          `,
          tags: ['onboarding', 'basics', 'dashboard', 'navigation'],
          readTime: 3,
          updatedAt: '2026-01-30'
        },
        {
          slug: 'setting-up-your-profile',
          title: 'Setting Up Your Profile',
          summary: 'Add your photo, contact info, and customize how buyers see you.',
          content: `
  # Setting Up Your Profile
  
  Your profile is what buyers see when they're matched with you. Make a great first impression!
  
  ## Profile Photo
  
  1. Click on your profile card on the Dashboard
  2. Click the camera icon or "Update Photo"
  3. Upload a professional headshot (JPG or PNG, up to 10MB)
  4. Crop and adjust as needed
  5. Click "Save Photo"
  
  **Tips for a great photo:**
  - Use a professional headshot with good lighting
  - Smile! Buyers want to work with friendly agents
  - Avoid busy backgrounds
  - Make sure your face is clearly visible
  
  ## Contact Information
  
  Your contact info is shown to buyers you're assigned to:
  
  - **Alias Email** — A Lauft-provided email that forwards to your real address
  - **Phone Number** — Your business phone for buyer calls
  
  To update your phone number, contact Lauft support.
  
  ## Referral Link & QR Code
  
  Every agent gets a unique referral link. Share it to:
  
  - Invite new buyers to download Lauft
  - Track which users came from your marketing
  - Build your client pipeline
  
  Find your referral link and QR code in the profile section of your dashboard.
          `,
          tags: ['profile', 'photo', 'setup', 'contact'],
          readTime: 4,
          updatedAt: '2026-01-28'
        },
        {
          slug: 'understanding-your-dashboard',
          title: 'Understanding Your Dashboard',
          summary: 'Learn what each metric means and how to use your dashboard effectively.',
          content: `
  # Understanding Your Dashboard
  
  Your dashboard gives you real-time insights into your Lauft business. Here's what each section means:
  
  ## Stats Overview
  
  ### Assigned Buyers
  The total number of buyers currently in your pipeline. These are users who have been matched with you and are actively looking for homes.
  
  ### Available Users
  Users in your area who are ready to be claimed. Click "View All" to browse and claim new leads.
  
  ### Matches Sent
  The number of curated property matches you've sent to your buyers. More matches = more engagement!
  
  ### Invited Users
  Users who signed up through your referral link. Track your marketing effectiveness here.
  
  ## Available Users Preview
  
  This section shows up to 7 available leads in your area. Each card shows:
  
  - User's name and personality type
  - Location preferences
  - Budget range
  - How long they've been active
  
  Click a user to view their full profile and claim them.
  
  ## Recent Properties
  
  A quick view of your latest listings. Click "View All" to manage your full property list.
  
  ## Pro Tips
  
  - Check your dashboard daily to catch new available users
  - Send at least one curated match per week to keep buyers engaged
  - Monitor your assigned buyers count — it's your active pipeline
          `,
          tags: ['dashboard', 'metrics', 'stats', 'overview'],
          readTime: 5,
          updatedAt: '2026-01-25'
        }
      ]
    },
    {
      slug: 'user-management',
      title: 'Managing Users',
      icon: 'Users',
      description: 'Invitations, leads, and buyer relationships.',
      color: '#8b5cf6', // Purple
      articles: [
        {
          slug: 'inviting-buyers',
          title: 'Inviting Buyers to Lauft',
          summary: 'Learn how to send invitations and grow your buyer pipeline.',
          content: `
  # Inviting Buyers to Lauft
  
  Growing your pipeline starts with invitations. Here's how to bring new buyers onto the platform.
  
  ## Sending an Invitation
  
  1. Go to **User Management** in the sidebar
  2. Fill out the invitation form:
     - **Recipient Name** — The buyer's full name
     - **Email** — Their email address
     - **Phone** (optional) — Their phone number
     - **Personal Message** (optional) — A custom note
  3. Click **Send Invitation**
  
  The buyer will receive an email with a link to download Lauft and create their account.
  
  ## Tracking Invitations
  
  Below the form, you'll see your **Recent Invitations** table showing:
  
  - Recipient name and contact info
  - Invitation status (Pending, Accepted, Expired)
  - When it was sent
  
  ## Referral Link
  
  For broader outreach, use your referral link:
  
  1. Click **Copy Referral Link** on your dashboard
  2. Share it via email, social media, or your website
  3. Anyone who signs up through your link is tracked as your referral
  
  ## QR Code
  
  Generate a QR code that links to your referral URL — perfect for business cards, flyers, or open houses.
  
  ## Tips for Success
  
  - Personalize your invitation message
  - Follow up with a phone call after sending
  - Share your referral link on your social profiles
  - Include your QR code on all marketing materials
          `,
          tags: ['invitations', 'buyers', 'referral', 'marketing'],
          readTime: 4,
          updatedAt: '2026-01-29'
        },
        {
          slug: 'claiming-leads',
          title: 'Claiming Available Leads',
          summary: 'How to browse, evaluate, and claim new buyers from the lead pool.',
          content: `
  # Claiming Available Leads
  
  The Available Users section is your gateway to new clients. Here's how to make the most of it.
  
  ## Browsing Available Users
  
  1. Click **Available Users** in the sidebar
  2. Browse the list of users in your area
  3. Each card shows key info:
     - Name and personality type
     - Location preferences
     - Budget range
     - Activity level
  
  ## Evaluating a Lead
  
  Before claiming, consider:
  
  - **Budget Match** — Can you serve their price range?
  - **Location** — Are they looking in areas you cover?
  - **Personality Type** — Understanding their LauftQ personality helps you connect
  - **Activity** — Recently active users are more engaged
  
  Click on a user card to see their full profile with detailed preferences.
  
  ## Claiming a Lead
  
  1. Click on the user you want to claim
  2. Review their full profile
  3. Click **Claim This Lead**
  4. Confirm the claim
  
  Once claimed, the user moves to your Assigned Buyers and you can start communicating with them.
  
  ## Lead Points
  
  Claiming leads may cost points depending on your plan:
  
  - **Free Trial** — Limited claims included
  - **Standard Plan** — Points refresh monthly
  - **Premium Plan** — Unlimited claims
  
  Check your point balance on your dashboard.
          `,
          tags: ['leads', 'claiming', 'available users', 'points'],
          readTime: 4,
          updatedAt: '2026-01-27'
        },
        {
          slug: 'sending-curated-matches',
          title: 'Sending Curated Matches',
          summary: 'Hand-pick properties for your buyers to boost engagement.',
          content: `
  # Sending Curated Matches
  
  Curated matches are personalized property recommendations you send directly to your buyers. They're a powerful way to stay top-of-mind and demonstrate your expertise.
  
  ## Why Send Curated Matches?
  
  - Show buyers you're actively working for them
  - Highlight properties they might have missed
  - Demonstrate your knowledge of their preferences
  - Keep engagement high between conversations
  
  ## How to Send a Match
  
  1. Go to **User Management**
  2. Find the buyer in your **Assigned Buyers** list
  3. Click **Send Match** or the property icon
  4. Search or browse for properties to recommend
  5. Select one or more properties
  6. Add a personal note (optional)
  7. Click **Send**
  
  The buyer receives a push notification and email with your recommendations.
  
  ## Best Practices
  
  - Send matches weekly to stay engaged
  - Include a note explaining why you chose each property
  - Mix new listings with hidden gems
  - Pay attention to their personality type preferences
  - Follow up to ask what they thought
  
  ## Tracking Matches
  
  View your sent matches in the buyer's profile to see:
  
  - Which properties you've recommended
  - Whether they viewed the match
  - Any feedback or responses
          `,
          tags: ['matches', 'curated', 'recommendations', 'engagement'],
          readTime: 4,
          updatedAt: '2026-01-26'
        }
      ]
    },
    {
      slug: 'listings-properties',
      title: 'Listings & Properties',
      icon: 'Home',
      description: 'Adding, editing, and managing your listings.',
      color: '#10b981', // Emerald
      articles: [
        {
          slug: 'adding-a-property',
          title: 'Adding a Property Manually',
          summary: 'Step-by-step guide to adding listings to your portfolio.',
          content: `
  # Adding a Property Manually
  
  While MLS sync handles most listings automatically, you can add properties manually for pocket listings, coming-soons, or special situations.
  
  ## Adding a New Property
  
  1. Click **Add Property** in the sidebar
  2. Fill out the property details:
  
  ### Basic Information
  - **Address** — Full street address
  - **City, State, ZIP** — Location details
  - **Price** — Listing price
  - **Property Type** — House, Condo, Townhouse, etc.
  
  ### Property Details
  - **Bedrooms & Bathrooms**
  - **Square Footage**
  - **Year Built**
  - **Lot Size**
  
  ### Features & Amenities
  - Select applicable features (pool, garage, etc.)
  - Add custom features as needed
  
  ### Photos
  - Upload high-quality images
  - First image becomes the main photo
  - Drag to reorder
  
  ### Description
  - Write a compelling property description
  - Highlight unique selling points
  
  3. Click **Save Property**
  
  ## Editing Properties
  
  1. Go to **Additional Listings**
  2. Find the property and click **Edit**
  3. Make your changes
  4. Click **Save**
  
  ## Tips for Great Listings
  
  - Use professional photos when possible
  - Write descriptions that paint a picture
  - Highlight neighborhood amenities
  - Keep information accurate and up-to-date
          `,
          tags: ['properties', 'listings', 'adding', 'manual'],
          readTime: 5,
          updatedAt: '2026-01-28'
        },
        {
          slug: 'mls-sync',
          title: 'Understanding MLS Sync',
          summary: 'How your MLS listings automatically appear in Lauft.',
          content: `
  # Understanding MLS Sync
  
  Lauft automatically syncs listings from your MLS, keeping your property portfolio up-to-date without manual work.
  
  ## How It Works
  
  1. **Connection** — Your brokerage connects Lauft to your MLS feed
  2. **Sync** — Listings are automatically imported and updated
  3. **Matching** — Properties are matched to buyers based on preferences
  
  ## What Gets Synced
  
  - All active listings in your MLS
  - Property details, photos, and descriptions
  - Price changes and status updates
  - New listings as they're added
  
  ## Sync Frequency
  
  MLS data typically syncs every 15-30 minutes, depending on your MLS provider.
  
  ## Troubleshooting
  
  ### Listing Not Appearing?
  
  - Allow up to 1 hour for new listings to sync
  - Verify the listing is active in your MLS
  - Check that your MLS connection is active
  
  ### Incorrect Information?
  
  - Updates must be made in your MLS first
  - Changes will sync automatically
  - Contact support if issues persist
  
  ### Missing Photos?
  
  - Ensure photos are uploaded in your MLS
  - Some MLS feeds have photo delays
  - High-resolution images may take longer
  
  ## Manual Additions
  
  For listings not in your MLS (pocket listings, etc.), use the **Add Property** feature to add them manually.
          `,
          tags: ['mls', 'sync', 'listings', 'automatic'],
          readTime: 4,
          updatedAt: '2026-01-24'
        }
      ]
    },
    {
      slug: 'showings-scheduling',
      title: 'Showings & Scheduling',
      icon: 'Calendar',
      description: 'Managing showing requests and appointments.',
      color: '#f59e0b', // Amber
      articles: [
        {
          slug: 'managing-showing-requests',
          title: 'Managing Showing Requests',
          summary: 'How to view, approve, and manage showing requests from buyers.',
          content: `
  # Managing Showing Requests
  
  When buyers want to see a property, they submit a showing request through the app. Here's how to manage them.
  
  ## Viewing Requests
  
  1. Click **Scheduled Showings** in the sidebar
  2. See all pending and confirmed requests
  3. Each request shows:
     - Buyer name and contact info
     - Property address
     - Requested date/time
     - Status (Pending, Confirmed, Completed, Cancelled)
  
  ## Responding to Requests
  
  ### To Confirm a Showing:
  1. Click on the pending request
  2. Review the details
  3. Click **Confirm Showing**
  4. The buyer receives a confirmation notification
  
  ### To Suggest a Different Time:
  1. Click on the request
  2. Click **Suggest Alternative**
  3. Select a new date/time
  4. Add a message explaining the change
  5. The buyer can accept or propose another time
  
  ### To Decline:
  1. Click on the request
  2. Click **Decline**
  3. Optionally add a reason
  4. Consider suggesting an alternative
  
  ## After the Showing
  
  Mark showings as completed to track your activity:
  
  1. Find the confirmed showing
  2. Click **Mark Complete**
  3. Add notes about the showing (optional)
  
  ## Tips for Success
  
  - Respond to requests within 2 hours when possible
  - Confirm showings at least 24 hours in advance
  - Send a reminder message the day before
  - Follow up after every showing
          `,
          tags: ['showings', 'scheduling', 'requests', 'appointments'],
          readTime: 4,
          updatedAt: '2026-01-29'
        },
        {
          slug: 'showing-notifications',
          title: 'Showing Notifications',
          summary: 'Stay on top of requests with email and push notifications.',
          content: `
  # Showing Notifications
  
  Never miss a showing request with Lauft's notification system.
  
  ## How Notifications Work
  
  When a buyer requests a showing, you'll receive:
  
  1. **Push Notification** — Instant alert on your phone
  2. **Email** — Detailed email with request info
  3. **Dashboard Alert** — Badge on Scheduled Showings tab
  
  ## Email Notifications Include
  
  - Buyer name and contact info
  - Property address with link
  - Requested date and time
  - Quick action buttons to confirm or reschedule
  - Link to view in your portal
  
  ## Managing Notification Preferences
  
  Currently, all showing notifications are enabled by default. Contact support if you need to adjust your preferences.
  
  ## Response Time Matters
  
  Buyers expect quick responses:
  
  - **Under 1 hour** — Excellent, shows you're attentive
  - **1-4 hours** — Good, still timely
  - **Over 4 hours** — May lose buyer interest
  
  Set up email alerts on your phone to catch requests quickly.
          `,
          tags: ['notifications', 'showings', 'email', 'alerts'],
          readTime: 3,
          updatedAt: '2026-01-25'
        }
      ]
    },
    {
      slug: 'messaging',
      title: 'Messaging & Conversations',
      icon: 'MessageCircle',
      description: 'Communicating with your buyers.',
      color: '#06b6d4', // Cyan
      articles: [
        {
          slug: 'using-conversations',
          title: 'Using the Conversations Feature',
          summary: 'How to message your buyers directly through Lauft.',
          content: `
  # Using the Conversations Feature
  
  Stay connected with your buyers through Lauft's built-in messaging system.
  
  ## Accessing Conversations
  
  1. Click **Conversations** in the sidebar
  2. See all your active conversations
  3. Unread messages are highlighted
  
  ## Starting a Conversation
  
  Conversations are typically started by buyers, but you can initiate too:
  
  1. Go to **User Management**
  2. Find the buyer
  3. Click **Message** or the chat icon
  4. Type your message and send
  
  ## Conversation Features
  
  - **Real-time messaging** — Messages appear instantly
  - **Read receipts** — See when messages are read
  - **Property sharing** — Send listing links directly
  - **Photo sharing** — Share images in chat
  
  ## Best Practices
  
  - Respond within a few hours during business hours
  - Keep messages professional but friendly
  - Use the buyer's name
  - Be specific and helpful
  - Follow up on unanswered questions
  
  ## Notifications
  
  You'll receive notifications for new messages via:
  - Push notifications
  - Email alerts
  - Dashboard badges
  
  Check your conversations daily to stay responsive.
          `,
          tags: ['messaging', 'conversations', 'chat', 'communication'],
          readTime: 4,
          updatedAt: '2026-01-28'
        }
      ]
    },
    {
      slug: 'analytics',
      title: 'Analytics & Reports',
      icon: 'BarChart2',
      description: 'Understanding your performance metrics.',
      color: '#ec4899', // Pink
      articles: [
        {
          slug: 'understanding-analytics',
          title: 'Understanding Your Analytics',
          summary: 'Track your performance and identify growth opportunities.',
          content: `
  # Understanding Your Analytics
  
  Your analytics help you understand what's working and where to improve.
  
  ## Key Metrics
  
  ### Referral Performance
  - **Link Clicks** — How many times your referral link was clicked
  - **Conversions** — How many clicks became registered users
  - **Conversion Rate** — Percentage of clicks that converted
  
  ### Buyer Engagement
  - **Assigned Buyers** — Your current client count
  - **Active Buyers** — Buyers who logged in this week
  - **Matches Sent** — Properties you've recommended
  - **Match Opens** — How many matches were viewed
  
  ### Showing Activity
  - **Requests Received** — Total showing requests
  - **Showings Completed** — Confirmed and finished
  - **Response Time** — Average time to respond
  
  ## Using Analytics
  
  ### Identify Trends
  - Which marketing channels drive the most referrals?
  - What times do buyers engage most?
  - Which property types get the most interest?
  
  ### Improve Performance
  - Low conversion rate? Improve your referral messaging
  - Few active buyers? Send more curated matches
  - Slow response time? Set up mobile notifications
  
  ## Exporting Data
  
  Need to analyze further? Contact support to request a data export.
          `,
          tags: ['analytics', 'metrics', 'performance', 'reports'],
          readTime: 4,
          updatedAt: '2026-01-26'
        }
      ]
    },
    {
      slug: 'faqs',
      title: 'FAQs',
      icon: 'HelpCircle',
      description: 'Quick answers to common questions.',
      color: '#64748b', // Slate
      articles: [
        {
          slug: 'account-billing',
          title: 'Account & Billing FAQ',
          summary: 'Common questions about your account, plans, and payments.',
          content: `
  # Account & Billing FAQ
  
  ## What plans are available?
  
  Lauft offers several plans for agents:
  
  - **Free Trial** — Limited features to try the platform
  - **Standard** — Full features with monthly point allocation
  - **Premium** — Unlimited access and priority support
  
  Contact your brokerage or Lauft sales for pricing.
  
  ## How do I upgrade my plan?
  
  Click the "Trial Account" badge (if visible) or contact support to discuss upgrade options.
  
  ## What are points used for?
  
  Points are used to claim leads from the Available Users pool. Different plans include different point allocations.
  
  ## How do I change my password?
  
  1. Go to your Dashboard
  2. Click the settings/profile area
  3. Select "Change Password"
  4. Enter your current password and new password
  5. Click "Save"
  
  ## How do I update my email?
  
  Contact Lauft support to update your account email address.
  
  ## Can I have multiple agent accounts?
  
  Each agent should have one account. For team setups, ask about our Team Lead features.
          `,
          tags: ['account', 'billing', 'plans', 'faq'],
          readTime: 3,
          updatedAt: '2026-01-30'
        },
        {
          slug: 'technical-issues',
          title: 'Technical Issues FAQ',
          summary: 'Troubleshooting common technical problems.',
          content: `
  # Technical Issues FAQ
  
  ## The portal won't load
  
  1. Clear your browser cache
  2. Try a different browser (Chrome recommended)
  3. Check your internet connection
  4. Disable browser extensions temporarily
  
  ## I can't log in
  
  1. Verify your email address is correct
  2. Try "Forgot Password" to reset
  3. Check if Caps Lock is on
  4. Contact support if issues persist
  
  ## Photos won't upload
  
  - Ensure file is JPG or PNG
  - File size must be under 10MB
  - Try a different browser
  - Check your internet connection
  
  ## Notifications aren't working
  
  - Check your email spam folder
  - Verify email address is correct
  - Enable push notifications in your browser
  - Check notification settings on your phone
  
  ## The app is slow
  
  - Clear browser cache
  - Close other browser tabs
  - Try a different browser
  - Check your internet speed
  
  ## Still having issues?
  
  Contact Lauft support through:
  - The chat widget on your dashboard
  - Email: support@lauft.app
  - Your account manager
          `,
          tags: ['technical', 'troubleshooting', 'issues', 'faq'],
          readTime: 3,
          updatedAt: '2026-01-29'
        },
        {
          slug: 'team-features',
          title: 'Team Features FAQ',
          summary: 'Questions about team leads and team management.',
          content: `
  # Team Features FAQ
  
  ## What is a Team Lead?
  
  Team Leads can view and manage other agents on their team. This includes:
  
  - Viewing team member performance
  - Monitoring team conversations
  - Managing team assignments
  
  ## How do I become a Team Lead?
  
  Team Lead status is assigned by your brokerage admin or Lauft support. Contact your broker to request Team Lead access.
  
  ## What can I see as a Team Lead?
  
  - Team member list and basic stats
  - Aggregate team performance metrics
  - Individual member activity summaries
  
  ## Can I reassign buyers between team members?
  
  This feature is coming soon. Currently, contact support for reassignment requests.
  
  ## How do I add someone to my team?
  
  Team membership is managed by your brokerage admin. Contact them to add new team members.
          `,
          tags: ['team', 'team lead', 'management', 'faq'],
          readTime: 3,
          updatedAt: '2026-01-27'
        }
      ]
    }
  ];
  
  // Helper function to get a category by slug
  export const getCategoryBySlug = (slug) => {
    return supportCategories.find(c => c.slug === slug);
  };
  
  // Helper function to get an article by category and article slug
  export const getArticle = (categorySlug, articleSlug) => {
    const category = getCategoryBySlug(categorySlug);
    if (!category) return null;
    return category.articles.find(a => a.slug === articleSlug);
  };
  
  // Helper function to get total article count for a category
  export const getArticleCount = (categorySlug) => {
    const category = getCategoryBySlug(categorySlug);
    return category ? category.articles.length : 0;
  };
  
  // Helper function to search all articles
  export const searchArticles = (query) => {
    if (!query || query.trim() === '') return [];
    
    const q = query.toLowerCase().trim();
    const results = [];
    
    supportCategories.forEach(category => {
      category.articles.forEach(article => {
        const titleMatch = article.title.toLowerCase().includes(q);
        const summaryMatch = article.summary.toLowerCase().includes(q);
        const tagMatch = article.tags.some(tag => tag.toLowerCase().includes(q));
        const contentMatch = article.content.toLowerCase().includes(q);
        
        if (titleMatch || summaryMatch || tagMatch || contentMatch) {
          // Calculate relevance score
          let score = 0;
          if (titleMatch) score += 10;
          if (summaryMatch) score += 5;
          if (tagMatch) score += 3;
          if (contentMatch) score += 1;
          
          results.push({
            ...article,
            categorySlug: category.slug,
            categoryTitle: category.title,
            categoryColor: category.color,
            relevanceScore: score
          });
        }
      });
    });
    
    // Sort by relevance score
    return results.sort((a, b) => b.relevanceScore - a.relevanceScore);
  };
  
  // Helper function to get all articles (flat list)
  export const getAllArticles = () => {
    const articles = [];
    supportCategories.forEach(category => {
      category.articles.forEach(article => {
        articles.push({
          ...article,
          categorySlug: category.slug,
          categoryTitle: category.title,
          categoryColor: category.color
        });
      });
    });
    return articles;
  };
  
  // Helper function to get recent/popular articles
  export const getRecentArticles = (limit = 5) => {
    const allArticles = getAllArticles();
    return allArticles
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .slice(0, limit);
  };
  
  export default supportCategories;